import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  CheckIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { useRef, useEffect } from 'react';

import { Button } from '../primitives';
import { Spinner } from '../Spinner';

import './Modal.css';
import { ActionButton } from '../types';

export interface IModalProps {
  title: string;
  titleHidden?: boolean;
  icon?: React.ReactNode | ('success' | 'warning' | 'error' | 'info');
  open?: boolean;
  loading?: boolean;
  size?: 'small' | 'large' | 'xlarge';
  centered?: boolean;
  primaryAction?: ActionButton;
  secondaryActions?: ActionButton[] | ActionButton;
  onClose: () => void;
  footer?: boolean;
  onScrollEnd?: () => void;
}

const icons: { [key: string]: React.ReactNode } = {
  warning: <ExclamationCircleIcon />,
  success: <CheckIcon />,
  error: <ExclamationTriangleIcon />,
  info: <InformationCircleIcon />,
};

/**
 * Legacy modal API on a Radix Dialog. Focus trap, Escape handling, outside
 * click dismissal, and aria wiring come from Radix; the markup keeps the
 * Modal.css class structure so consumers stay pixel-identical. The title
 * always renders as an h3 (visually hidden when titleHidden) — the
 * product-review e2e selects it by heading role.
 */
export const Modal: React.FC<React.PropsWithChildren<IModalProps>> = ({
  title,
  titleHidden = false,
  icon,
  children,
  open = false,
  loading = false,
  size = 'large',
  centered = false,
  primaryAction,
  secondaryActions,
  onClose,
  footer = false,
  onScrollEnd,
}) => {
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      const contentWrapper = contentWrapperRef.current;
      if (contentWrapper) {
        const scrollPosition = contentWrapper.scrollTop + contentWrapper.clientHeight;
        const threshold = contentWrapper.scrollHeight - 100; // Trigger when 100px from the bottom

        if (scrollPosition >= threshold && onScrollEnd) {
          onScrollEnd(); // Trigger the callback
        }
      }
    };

    const contentWrapper = contentWrapperRef.current;
    if (contentWrapper) {
      contentWrapper.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentWrapper) {
        contentWrapper.removeEventListener('scroll', handleScroll);
      }
    };
  }, [onScrollEnd, open]);

  const renderSecondaryActions = () => {
    if (secondaryActions == undefined) return null;
    if (Array.isArray(secondaryActions)) {
      return secondaryActions.map(({ label, ...props }, idx) => (
        <Button key={idx} {...props}>
          {label}
        </Button>
      ));
    }
    return (
      <Button
        variant={secondaryActions.variant}
        icon={secondaryActions.icon}
        iconPosition={secondaryActions.iconPosition}
        loading={secondaryActions.loading}
        onClick={secondaryActions.onClick}
        disabled={secondaryActions.disabled}
      >
        {secondaryActions.label}
      </Button>
    );
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="modal-overlay" />
        <div className="modal-dialog">
          <div
            className={`modal-dialog-container ${
              centered ? 'modal-dialog-container-centered' : ''
            } modal-${size}`}
          >
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <DialogPrimitive.Content
              className="relative modal-container"
              aria-describedby={undefined}
            >
              {loading && <Spinner size="xlarge" centerOverContent />}
              <div className={`${loading ? 'opacity-10 pointer-events-none' : ''}`}>
                {(!titleHidden || centered) && onClose && (
                  <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                    <button
                      type="button"
                      className="modal-close-button"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {/* Modal Header */}
                <div className="modal-header">
                  {icon && (
                    <div
                      className={`modal-icon-container ${
                        typeof icon === 'string' ? `modal-${icon}` : ''
                      }`}
                    >
                      <span className="modal-icon">
                        {typeof icon === 'string' ? icons[icon] : icon}
                      </span>
                    </div>
                  )}
                  <DialogPrimitive.Title asChild>
                    <h3 className={titleHidden ? 'sr-only' : 'modal-title'}>{title}</h3>
                  </DialogPrimitive.Title>
                </div>

                {/* Modal Content */}
                <div className="modal-content-wrapper" ref={contentWrapperRef}>
                  <div className="modal-content">{children}</div>
                </div>

                {/* Modal Footer */}
                {(primaryAction || secondaryActions) && (
                  <div className={`modal-footer ${footer ? 'bg-neutral-50 ' : '-mt-3 mb-3'}`}>
                    {renderSecondaryActions()}
                    {primaryAction && (
                      <Button
                        variant={primaryAction.variant}
                        icon={primaryAction.icon}
                        iconPosition={primaryAction.iconPosition}
                        loading={primaryAction.loading}
                        onClick={primaryAction.onClick}
                        disabled={primaryAction.disabled}
                      >
                        {primaryAction.label}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </DialogPrimitive.Content>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
